@if (control(); as control) {
  @if (field(); as field) {
    <mat-form-field class="w-full" color="accent">
      @if (field.type === FormControlType.AUTO_COMPLETE && field.displaySearchIcon?.()) {
        <mat-icon class="iot-platform-ui-form-field__search-icon" matPrefix>search</mat-icon>
      }

      @if (field.label?.(); as label) {
        <mat-label [ngClass]="{ 'capitalize-first-letter': $safeNavigationMigration(field.capitalized?.()) }">{{ label | translate }}</mat-label>
      }

      <!-- field.autocomplete?.() can be undefined -->
      @if (field.autocomplete?.() === false) {
        <input
          (focus)="field.onFocus?.($event)"
          (click)="field.onClick?.($event)"
          (keyup.enter)="field.onKeyupEnter?.($event)"
          [maxlength]="field.maxLength?.() ?? null"
          [minlength]="field.minLength?.() ?? null"
          [pattern]="field.pattern?.() ?? null"
          [formControl]="control"
          [required]="required()"
          [placeholder]="$safeNavigationMigration(field.placeholder?.())"
          #inputText
          class="iot-platform-ui-form-field--{{ field.name?.() }} {{ field.inputCssClassName?.() }}"
          data-cy="form-control"
          matInput
          [textMask]="$safeNavigationMigration(field.textMask?.())"
          [matTooltip]="$safeNavigationMigration(control?.value)"
          [matTooltipDisabled]="inputText.scrollWidth <= inputText.clientWidth"
          matTooltipClass="regular-tooltip"
          matTooltipPosition="above"
          type="text"
        />
      } @else {
        <input
          #inputAutoComplete
          class="iot-platform-ui-form-field--{{ field.name?.() }} {{ field.inputCssClassName?.() }}"
          data-cy="form-control"
          [maxlength]="field.maxLength?.() ?? null"
          [minlength]="field.minLength?.() ?? null"
          [pattern]="field.pattern?.() ?? null"
          [formControl]="control"
          [required]="required()"
          matInput
          [matAutocomplete]="auto"
          (input)="onFilterInput($event)"
          (focus)="filterAutocomplete(displayByFn($safeNavigationMigration(control?.getRawValue()))); field.onFocus?.($event)"
          (click)="field.onClick?.($event)"
          (keyup.enter)="field.onKeyupEnter?.($event)"
          (mouseenter)="checkAutocompleteTooltip(inputAutoComplete)"
          [placeholder]="$safeNavigationMigration(field.placeholder?.())"
          [matTooltip]="displayByFn($safeNavigationMigration(control?.value))"
          [matTooltipDisabled]="autocompleteTooltipDisabled()"
          matTooltipClass="regular-tooltip"
          matTooltipPosition="above"
        />
      }

      <div
        matSuffix
        class="iot-platform-ui-form-field__autocomplete-clear-search-control flex flex-row gap-[6px]"
        [ngClass]="{ 'iot-platform-ui-form-field__autocomplete-clear-search-control--text': !field.autocomplete?.() }"
      >
        @if (field.showSpinner?.() || controlStatus() === 'PENDING') {
          <mat-spinner class="iot-platform-ui-form-field__autocomplete-clear-search-control_spinner" color="accent" diameter="16" mode="indeterminate" />
        }
        @if ($safeNavigationMigration(control?.value) !== undefined && $safeNavigationMigration(control?.value) !== null && control?.value !== '') {
          <button
            class="iot-platform-ui-form-field__autocomplete-clear-search-control_action"
            data-cy="iot-platform-ui-form-field-autocomplete-clear-search-control-action"
            (click)="clearAutocomplete($event)"
            [disabled]="$safeNavigationMigration(field.disabled?.())"
            aria-label="Clear"
            mat-icon-button
            type="button"
          >
            <mat-icon>clear</mat-icon>
          </button>
        }
      </div>

      <mat-autocomplete
        #auto="matAutocomplete"
        (optionSelected)="onAutoCompleteOptionsSelected($event)"
        [displayWith]="displayByFn"
        [requireSelection]="field.requireSelection?.() ?? false"
        class="{{ field.inputCssClassName?.() }}"
      >
        @if (field.optionGroups?.()?.length) {
          @for (group of autocompleteFilteredOptionGroups(); track group.label) {
            <mat-optgroup [label]="group.label">
              @for (item of group.items; track $index) {
                <mat-option
                  (click)="field.multiple?.() && $event.preventDefault(); $event.stopPropagation()"
                  [value]="field.multiple?.() ? autocompleteSelectedItems() : field.valueBy ? field.valueBy(item) : item"
                  [matTooltip]="field.tooltip?.enabled?.() ? $safeNavigationMigration(field.displayBy?.(item)) : ''"
                  [disabled]="!field.multiple?.() && field.disabled?.()"
                  matTooltipClass="regular-tooltip"
                  matTooltipPosition="right"
                  class="iot-platform-ui-form-field__autocomplete-option iot-platform-ui-form-field--{{ field.name?.() }}-autocomplete-option {{
                    field.inputCssClassName?.()
                  }}"
                  data-cy="iot-platform-ui-form-field-autocomplete-option"
                >
                  @if (field.selectDisplayWrapperRef?.()) {
                    <ng-container [ngTemplateOutlet]="$safeNavigationMigration(field.selectDisplayWrapperRef?.())" [ngTemplateOutletContext]="{ item: item }" />
                  } @else if (field.multiple?.()) {
                    <mat-checkbox
                      (click)="$event.stopPropagation(); toggleAutoCompleteSelection(item)"
                      [checked]="item.selected"
                      [disabled]="!item.selected && field.disabled?.()"
                    >
                      {{ field.displayBy?.(item) }}
                    </mat-checkbox>
                  } @else {
                    {{ field.displayBy?.(item) }}
                  }
                </mat-option>
              }
            </mat-optgroup>
          }
        } @else {
          @for (item of autocompleteFilteredOptions(); track $index) {
            <mat-option
              (click)="field.multiple?.() && $event.preventDefault(); $event.stopPropagation()"
              [value]="field.multiple?.() ? autocompleteSelectedItems() : field.valueBy ? field.valueBy(item) : item"
              [matTooltip]="field.tooltip?.enabled?.() ? $safeNavigationMigration(field.displayBy?.(item)) : ''"
              [disabled]="!field.multiple?.() && field.disabled?.()"
              matTooltipClass="regular-tooltip"
              matTooltipPosition="right"
              class="iot-platform-ui-form-field__autocomplete-option iot-platform-ui-form-field--{{ field.name?.() }}-autocomplete-option {{
                field.inputCssClassName?.()
              }}"
              data-cy="iot-platform-ui-form-field-autocomplete-option"
            >
              @if (field.selectDisplayWrapperRef?.()) {
                <ng-container [ngTemplateOutlet]="$safeNavigationMigration(field.selectDisplayWrapperRef?.())" [ngTemplateOutletContext]="{ item: item }" />
              } @else if (field.multiple?.()) {
                <mat-checkbox
                  (click)="$event.stopPropagation(); toggleAutoCompleteSelection(item)"
                  [checked]="item.selected"
                  [disabled]="!item.selected && field.disabled?.()"
                >
                  {{ field.displayBy?.(item) }}
                </mat-checkbox>
              } @else {
                {{ field.displayBy?.(item) }}
              }
            </mat-option>
          }
        }
      </mat-autocomplete>

      @if (control?.invalid && control?.hasError('required')) {
        <mat-error class="!flex">
          <ng-container *ngTemplateOutlet="errorTpl; context: { message: 'FORM.ERROR_MESSAGE.REQUIRED' | translate }" />
        </mat-error>
      } @else if (control?.invalid && control?.hasError('maxlength')) {
        <mat-error class="!flex">
          <ng-container
            *ngTemplateOutlet="
              errorTpl;
              context: {
                message: 'FORM.ERROR_MESSAGE.MAX_LENGTH' | translate: { value: $safeNavigationMigration(control.getError('maxlength')?.requiredLength) }
              }
            "
          />
        </mat-error>
      } @else if (control?.invalid && control?.hasError('minlength')) {
        <mat-error class="!flex">
          <ng-container
            *ngTemplateOutlet="
              errorTpl;
              context: {
                message: 'FORM.ERROR_MESSAGE.MIN_LENGTH' | translate: { value: $safeNavigationMigration(control.getError('minlength')?.requiredLength) }
              }
            "
          />
        </mat-error>
      } @else if (control?.invalid && control?.hasError('max')) {
        <mat-error class="!flex">
          <ng-container
            *ngTemplateOutlet="
              errorTpl;
              context: { message: 'FORM.ERROR_MESSAGE.MAX' | translate: { value: $safeNavigationMigration(control.getError('max')?.max) } }
            "
          />
        </mat-error>
      } @else if (control?.invalid && control?.hasError('min')) {
        <mat-error class="!flex">
          <ng-container
            *ngTemplateOutlet="
              errorTpl;
              context: { message: 'FORM.ERROR_MESSAGE.MIN' | translate: { value: $safeNavigationMigration(control.getError('min')?.min) } }
            "
          />
        </mat-error>
      } @else if (control?.invalid && control?.hasError('pattern')) {
        <mat-error class="!flex">
          <ng-container *ngTemplateOutlet="errorTpl; context: { message: 'FORM.ERROR_MESSAGE.PATTERN' | translate }" />
        </mat-error>
      } @else if (control?.invalid && (control?.hasError('duplicateName') || control?.hasError('nameExists'))) {
        <mat-error class="!flex">
          <ng-container *ngTemplateOutlet="errorTpl; context: { message: 'FORM.ERROR_MESSAGE.DUPLICATE_NAME' | translate }" />
        </mat-error>
      } @else if (control?.invalid && control?.hasError('duplicateId')) {
        <mat-error class="!flex">
          <ng-container *ngTemplateOutlet="errorTpl; context: { message: 'FORM.ERROR_MESSAGE.DUPLICATE_ID' | translate }" />
        </mat-error>
      } @else if (control?.invalid && control?.hasError('link')) {
        <mat-error class="!flex">
          <ng-container *ngTemplateOutlet="errorTpl; context: { message: 'FORM.ERROR_MESSAGE.LINK' | translate }" />
        </mat-error>
      } @else if (control?.invalid && field.error?.enabled?.() && field.error?.message?.()) {
        <mat-error class="!flex">
          <ng-container *ngTemplateOutlet="errorTpl; context: { message: $safeNavigationMigration(field.error?.message?.()) | translate }" />
        </mat-error>
      }

      @if (field.prefix) {
        <mat-icon
          class="prefix-icon cursor-pointer"
          [ngClass]="{ 'animate-spin': $safeNavigationMigration(field.prefix?.loading?.()), invisible: field.prefix?.visible && !field.prefix?.visible() }"
          style="margin-right: 5px"
          [matTooltip]="field.prefix?.tooltip?.() ?? '' | translate"
          matPrefix
          matTooltipClass="regular-tooltip"
          matTooltipPosition="below"
          (click)="field?.prefix?.onClick?.($event)"
          >{{ field.prefix?.icon?.() }}
        </mat-icon>
      }

      @if (field.suffix) {
        <mat-icon
          class="suffix-icon cursor-pointer"
          [ngClass]="{ 'animate-spin': $safeNavigationMigration(field.suffix?.loading?.()), invisible: field.suffix?.visible && !field.suffix?.visible() }"
          style="margin-left: 5px"
          [matTooltip]="field.suffix?.tooltip?.() ?? '' | translate"
          matSuffix
          matTooltipClass="regular-tooltip"
          matTooltipPosition="above"
          (click)="field?.suffix?.onClick?.($event)"
          >{{ field.suffix?.icon?.() }}
        </mat-icon>
      }

      @if (field.hint?.()?.type?.() === FormControlHintType.TEXT) {
        <mat-hint align="end">
          {{ $safeNavigationMigration(field.hint?.()?.text?.()) | translate }}
        </mat-hint>
      } @else if (field.hint?.()?.type?.() === FormControlHintType.MAX_LENGTH) {
        <mat-hint align="end"> {{ control?.getRawValue()?.length ?? 0 }} / {{ field.maxLength?.() }}</mat-hint>
      } @else if (field.hint?.()?.type?.() === FormControlHintType.TEMPLATE_REF) {
        <mat-hint align="end">
          <ng-container [ngTemplateOutlet]="$safeNavigationMigration(field.hint?.()?.templateRef?.())" />
        </mat-hint>
      }
    </mat-form-field>
  }
}

<ng-template #errorTpl let-message="message">
  <span
    #errorText
    class="truncate"
    [matTooltip]="message"
    [matTooltipDisabled]="errorText.scrollWidth <= errorText.clientWidth"
    matTooltipClass="regular-tooltip"
    >{{ message }}</span
  >
</ng-template>
